/**
 * SRES · Satellite Keys Guide Card (Iter108 · Feb 2026).
 *
 * Admin-facing card that documents exactly which environment variables
 * (or Compliance Settings Store keys) the Sentinel Hub and Mapbox
 * providers require, plus a live "Test connection" button that hits
 * `/api/admin/imagery/status?live_probe=true` and colour-codes the
 * verdict per provider. Credential values are NEVER shown — only the
 * presence-mask + verdict returned by the backend.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Globe, ArrowsClockwise, CheckCircle, WarningCircle, Prohibit, Copy,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getImageryStatus } from "@/lib/api";


const PROVIDER_META = {
  mapbox: {
    label:       "Mapbox",
    description: ("Simple static-tiles API — recommended first choice. "
                    + "Free tier covers 50 000 static-image loads / month."),
    signupUrl:   "https://account.mapbox.com/",
    keyDocs:     "https://docs.mapbox.com/help/getting-started/access-tokens/",
  },
  sentinel_hub: {
    label:       "Sentinel Hub",
    description: ("Full Copernicus Sentinel-2 mosaic. Free trial + paid "
                    + "plans starting at ~€30/month. Better for scientific work."),
    signupUrl:   "https://apps.sentinel-hub.com/dashboard/",
    keyDocs:     ("https://docs.sentinel-hub.com/api/latest/api/overview/"
                    + "authentication/"),
  },
};

const STATE_META = {
  ready:              { colour: "emerald", label: "Ready",
                          icon: CheckCircle,
                          tone: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  partially_configured: { colour: "amber",
                          label: "Partially configured",
                          icon: WarningCircle,
                          tone: "border-amber-300 bg-amber-50 text-amber-800" },
  connection_failed:  { colour: "red", label: "Connection failed",
                          icon: WarningCircle,
                          tone: "border-red-300 bg-red-50 text-red-700" },
  not_configured:     { colour: "slate", label: "Not configured",
                          icon: Prohibit,
                          tone: "border-slate-300 bg-slate-50 text-slate-600" },
};


function _copy(v) {
  try {
    navigator.clipboard?.writeText?.(v);
    toast.success(`Copied ${v}`);
  } catch {
    toast.error("Copy failed");
  }
}


function ProviderRow({ p }) {
  const meta  = PROVIDER_META[p.provider] || { label: p.provider };
  const state = STATE_META[p.state]       || STATE_META.not_configured;
  const Icon  = state.icon;
  return (
    <div className={`rounded-md border p-3 ${state.tone}`}
             data-testid={`satellite-keys-provider-${p.provider}`}>
      <div className="flex items-center gap-2">
        <Icon size={16} weight="bold" />
        <span className="font-semibold text-[13.5px]">{meta.label}</span>
        <span className="ml-auto text-[10.5px] font-mono uppercase tracking-wider">
          {state.label}
        </span>
      </div>
      <p className="mt-1.5 text-[12px] text-slate-700">{meta.description}</p>
      <div className="mt-2 rounded border border-slate-200 bg-white p-2">
        <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-1.5">
          Required environment variables
        </div>
        <div className="space-y-1">
          {(p.required || []).map((env) => {
            const present = p.presence?.[env] === true;
            return (
              <div key={env}
                       className="flex items-center gap-2 text-[11.5px] font-mono"
                       data-testid={`satellite-keys-env-${env}`}>
                <span className={present
                                    ? "text-emerald-600"
                                    : "text-slate-400"}>
                  {present ? "●" : "○"}
                </span>
                <span className={present
                                    ? "text-slate-900"
                                    : "text-slate-500 line-through"}>
                  {env}
                </span>
                <button type="button"
                            onClick={() => _copy(env)}
                            className="ml-auto text-slate-400 hover:text-brand-blue"
                            aria-label={`Copy ${env}`}>
                  <Copy size={11} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
      {p.probe_note && (
        <div className="mt-1.5 text-[10.5px] text-slate-600 font-mono">
          probe · {p.probe_note}
        </div>
      )}
      <div className="mt-2 flex items-center gap-3 text-[11px]">
        <a href={meta.signupUrl} target="_blank" rel="noreferrer"
               className="text-brand-blue hover:underline">
          Sign up →
        </a>
        <a href={meta.keyDocs} target="_blank" rel="noreferrer"
               className="text-brand-blue hover:underline">
          Key docs →
        </a>
      </div>
    </div>
  );
}


export default function SatelliteKeysGuideCard() {
  const [state, setState]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);

  const load = useCallback(async (livProbe = false) => {
    (livProbe ? setProbing : setLoading)(true);
    try {
      const r = await getImageryStatus(livProbe);
      setState(r);
      if (livProbe) {
        if (r.any_ready)
          toast.success("At least one satellite provider is live · maps unlock automatically.");
        else
          toast.message("No provider is ready yet — add keys, then run the test again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message
                     || "Imagery status failed");
    } finally { (livProbe ? setProbing : setLoading)(false); }
  }, []);

  useEffect(() => { load(false); }, [load]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4"
             data-testid="satellite-keys-guide-card">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-brand-blue/10 flex items-center justify-center shrink-0">
          <Globe size={18} className="text-brand-blue" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">
            SRES · Remote sensing
          </div>
          <h2 className="font-heading font-bold text-[16px] tracking-tight text-slate-900 leading-tight">
            Satellite Keys Guide
          </h2>
          <p className="text-[12px] text-slate-600 mt-0.5">
            The CPR Section 1 Map Pack auto-activates a live satellite basemap
            (Figure 1.5) the moment either provider reaches state <b>ready</b>.
            Keys can be dropped into the container environment or the
            Compliance Settings Store — never in the frontend.
          </p>
        </div>
        <Button size="sm"
                    onClick={() => load(true)}
                    disabled={probing || loading}
                    className="bg-brand-blue text-white hover:opacity-90 text-[11.5px]"
                    data-testid="satellite-keys-test-btn">
          <ArrowsClockwise size={12}
                                 className={`mr-1 ${probing ? "animate-spin" : ""}`} />
          {probing ? "Testing…" : "Test connection"}
        </Button>
      </div>

      {loading && (
        <div className="mt-3 text-[12px] text-slate-500 italic">
          Loading imagery status…
        </div>
      )}

      {!loading && state && (
        <>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            {(state.providers || []).map((p) => (
              <ProviderRow key={p.provider} p={p} />
            ))}
          </div>
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2.5 text-[11.5px] text-slate-600"
                   data-testid="satellite-keys-note">
            {state.note}
          </div>
          <div className={`mt-2 text-[12px] font-semibold
                            ${state.any_ready ? "text-emerald-700" : "text-slate-500"}`}
                   data-testid="satellite-keys-any-ready">
            {state.any_ready
              ? "✓ At least one provider is live — CPR satellite basemap is active."
              : "◦ No provider is live yet — CPR still uses the documented placeholder card."}
          </div>
        </>
      )}
    </div>
  );
}
